import { ShieldCheck, ShieldAlert, ShieldQuestion } from "lucide-react";
import { useTaal } from "@/contexts/TaalContext";

/**
 * Wat de keten over EEN spoorregel zegt, in een zin die een mens kan lezen.
 *
 * Het oordeel komt van de server en wordt daar bij ELK lezen herberekend tegen
 * de hele keten van het gesprek. Hier wordt niets afgeleid en niets geraden;
 * deze component kiest alleen de woorden.
 *
 * DE DERDE TOESTAND IS DE MOEILIJKE. `unchained` betekent: deze regel is
 * geschreven voordat de recorder een keten bijhield, dus er valt niets te
 * controleren. Dat is iets anders dan goed en iets anders dan fout, en beide
 * verwarringen zijn schadelijk. Haar als goedkeuring tonen is letterlijk het
 * defect van buzz-db, dat elk event hard gecodeerd als geverifieerd teruggeeft
 * (buzz-db/src/event.rs:610-615). Haar als afkeuring tonen zou de 259 rijen die
 * op 21 augustus 2026 in Franks database stonden allemaal rood verven terwijl er
 * niets mis is met ze.
 *
 * EN DE GRENS STAAT IN DE TITEL. Er zijn geen sleutels: wie de database kan
 * schrijven, kan de hele keten herrekenen. Zonder die zin belooft een vinkje op
 * het scherm meer dan de keten waarmaakt. Buzz zegt het over hun eigen versie
 * net zo in hun SECURITY.md, en dat is de eerlijke grens op een machine van een
 * persoon.
 */

// GL217-7 (glasronde 0.9.216): de grens in de taal van de app. Was een vaste Engelse zin, ook in de NL-stand.
export const LIMIT = "Een wijziging valt op, maar is niet onmogelijk: er zijn geen sleutels, dus dit laat zien dat een regel is gewijzigd, niet dat hij niet gewijzigd kan worden.";

export const CHAIN_WORDS = {
  verified: {
    icon: ShieldCheck,
    text: "Nagekeken: ongewijzigd sinds het is geschreven",
    className: "text-ok",
  },
  broken: {
    icon: ShieldAlert,
    text: "Gewijzigd: dit klopt niet meer met wat is vastgelegd",
    className: "text-destructive",
  },
  unchained: {
    // Geen woord uit de andere twee. Niet "verified", niet "altered", niet
    // "broken": een lezer die deze zin snel leest, mag er geen oordeel uit
    // halen dat er niet is.
    icon: ShieldQuestion,
    text: "Geschreven voordat de recorder een keten bijhield, dus er valt niets na te kijken",
    className: "text-muted-foreground",
  },
};

export default function ChainState({ state, className = "" }) {
  const { t } = useTaal();
  const woorden = CHAIN_WORDS[state];
  // Een onbekende toestand toont NIETS. De server kan er later een bij krijgen,
  // en dan is zwijgen beter dan raden: een verkeerd geraden geruststelling is
  // precies wat deze keten moet voorkomen.
  if (!woorden) return null;

  const Icon = woorden.icon;
  return (
    <span
      data-testid="chain-state"
      title={t(LIMIT)}
      className={`inline-flex items-center gap-1 text-[11px] ${woorden.className} ${className}`}
    >
      <Icon className="h-3 w-3 shrink-0" />
      {t(woorden.text)}
    </span>
  );
}
